import React, { useEffect } from "react";
import "./AboutPage.css";

function AboutPage() {
  // On mount, if a hash is present in the URL, scroll smoothly to that section.
  useEffect(() => {
    if (window.location.hash) {
      const element = document.querySelector(window.location.hash);
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 100);
      }
    }
  }, []);

  return (
    <div className="about-page-about">
      {/* Header */}
      <header className="header-about">
        <h1 className="header-title-about">About Thompson Square Pharmacy</h1>
        <p className="header-subtitle-about">
          Your trusted partner in healthcare
        </p>
      </header>

      {/* Table of Contents */}
      <nav className="toc-about">
        <ul>
          <li>
            <a href="#about" className="toc-link-about">
              About Us
            </a>
          </li>
          <li>
            <a href="#compounding" className="toc-link-about">
              Compounding
            </a>
          </li>
          <li>
            <a href="#prescriptions" className="toc-link-about">
              Prescription Services
            </a>
          </li>
          <li>
            <a href="#medication-review" className="toc-link-about">
              Medication Review
            </a>
          </li>
          <li>
            <a href="#compliance-aids" className="toc-link-about">
              Compliance Aids
            </a>
          </li>
          <li>
            <a href="#medications-delivery" className="toc-link-about">
              Medications Delivery
            </a>
          </li>
          <li>
            <a href="#refill-reminders" className="toc-link-about">
              Refill Reminders
            </a>
          </li>
          <li>
            <a href="#hours-contact" className="toc-link-about">
              Hours & Contact
            </a>
          </li>
          <li>
            <a href="#sharps-disposal" className="toc-link-about">
              Sharps Disposal
            </a>
          </li>
        </ul>
      </nav>

      {/* Main Content */}
      <main className="main-about">
        {/* About Us */}
        <section id="about" className="section-about">
          <h2 className="section-title-about">About Us</h2>
          <p className="section-text-about">
            Thompson Square Pharmacy has proudly served our community with
            integrity, compassion, and innovative pharmaceutical services. Our
            dedicated team of pharmacists, technicians, and support staff works
            tirelessly to ensure that every patient receives personalized care
            tailored to their unique needs.
          </p>
          <p className="section-text-about">
            We believe that exceptional care goes beyond simply filling
            prescriptions—it’s about building long-term relationships, fostering
            trust, and continuously pursuing excellence in service.
          </p>
        </section>

        {/* Compounding */}
        <section id="compounding" className="section-about">
          <h2 className="section-title-about">Compounding</h2>
          <p className="section-text-about">
            Our state-of-the-art compounding services enable us to create custom
            medications specifically formulated for you. Whether you require a
            unique dosage form, flavor modifications for pediatric patients, or
            allergen-free alternatives, our expert pharmacists use the latest
            techniques and rigorous quality controls to deliver the perfect
            solution.
          </p>
          <p className="section-text-about">
            By collaborating closely with your healthcare provider, we ensure
            that every compounded medication is safe, effective, and tailored to
            maximize your therapeutic benefit.
          </p>
        </section>

        {/* Prescription Services */}
        <section id="prescriptions" className="section-about">
          <h2 className="section-title-about">Prescription Services</h2>
          <h3 className="subsection-title-about">Refills</h3>
          <p className="section-text-about">
            Our secure online refill system makes reordering your medications
            quick and convenient. Enjoy reliable service with minimal wait times
            and flexible pickup or delivery options.
          </p>
          <h3 className="subsection-title-about">New Prescriptions</h3>
          <p className="section-text-about">
            When you receive a new prescription, our streamlined process ensures
            prompt verification and processing. We work diligently to have your
            medications ready as soon as possible.
          </p>
          <h3 className="subsection-title-about">Transfers</h3>
          <p className="section-text-about">
            Switching pharmacies is now hassle‑free. Our prescription transfer
            service is designed to ensure a smooth transition so you can
            continue your treatment without interruption.
          </p>
        </section>

        {/* Free Medication Review */}
        <section id="medication-review" className="section-about">
          <h2 className="section-title-about">Free Medication Review</h2>
          <p className="section-text-about">
            Our complimentary medication review service is designed to optimize
            your treatment plan. Our pharmacists will assess your current
            medications, evaluate potential interactions, and provide
            personalized recommendations to improve your overall health
            outcomes.
          </p>
          <p className="section-text-about">
            This service empowers you with a clearer understanding of your
            therapies and helps ensure that your medication regimen is both safe
            and effective.
          </p>
        </section>

        {/* Compliance Aids / Blister Packaging */}
        <section id="compliance-aids" className="section-about">
          <h2 className="section-title-about">
            Compliance Aids &amp; Blister Packaging
          </h2>
          <p className="section-text-about">
            Managing multiple medications can be challenging. That’s why we
            offer tailored compliance aids, including blister packaging and pill
            organizers, to help you keep track of your daily doses. These tools
            are designed to simplify your regimen, ensuring you take the right
            medication at the right time.
          </p>
          <p className="section-text-about">
            With our support, you can reduce the risk of missed doses and
            improve the overall effectiveness of your treatment.
          </p>
        </section>

        {/* Medications Delivery */}
        <section id="medications-delivery" className="section-about">
          <h2 className="section-title-about">Medications Delivery</h2>
          <p className="section-text-about">
            For your convenience, we offer a reliable medications delivery
            service. Whether you prefer scheduled deliveries or need an urgent
            refill, our secure delivery system ensures your medications reach
            you safely and promptly.
          </p>
          <p className="section-text-about">
            Our dedicated delivery team works to make your experience seamless,
            saving you time and hassle.
          </p>
        </section>

        {/* Refill Reminders */}
        <section id="refill-reminders" className="section-about">
          <h2 className="section-title-about">Refill Reminders</h2>
          <p className="section-text-about">
            Our refill reminder service ensures that our staff contacts you when
            it’s time to reorder your medications, helping you maintain a
            consistent treatment plan. You can request a reminder via your
            preferred method—ensuring you never miss an important refill.
          </p>
        </section>

        {/* Sharps Disposal */}
        <section id="sharps-disposal" className="section-about">
          <h2 className="section-title-about">Sharps Disposal</h2>
          <h3 className="subsection-text-about">What is Accepted?</h3>
          <p className="section-text-about">
            Any hard shell plastic container that is leak and tamper-resistant,
            labelled with the universal biohazard symbol and is designed to hold
            needles and other medical devices containing a medical sharp.
          </p>
          <h3 className="subsection-text-about">Not accepted:</h3>
          <p className="section-text-about">
            Any non-conforming container such as bottles, tin cans, and laundry
            detergent containers.
          </p>
        </section>
        {/* Hours, Fax & Contact */}
        <section id="hours-contact" className="section-about">
          <h2 className="section-title-about">Hours, Fax &amp; Contact</h2>
          <p className="section-text-about">
            <strong>Hours of Operation:</strong>
            <br />
            Monday: 10:00 AM - 6:00 PM
            <br />
            Tuesday to Friday: 10:00 AM - 6:30 PM
            <br />
            Sat: 10:00 AM - 2:00 PM
            <br />
            Sunday: Closed
          </p>
          <p className="section-text-about">
            <strong>Address:</strong> 9137 Derry Rd W #1, Milton, ON L9T 7Z1,
            Canada
          </p>
          <p className="section-text-about">
            <strong>Fax:</strong> (905) 749-9001
          </p>
          <p className="section-text-about">
            <strong>Phone:</strong> (905) 749-9990 &nbsp; | &nbsp;{" "}
            <strong>Email:</strong> thompsonsquarepharmacy@gmail.com
          </p>
        </section>
      </main>
    </div>
  );
}

export default AboutPage;
